<template>
  <div v-if="donation && donation.paymentSpecification && donation.serialnumber" class="success-view">
    <iframe
      ref="printIframe"
      :key="donation.serialnumber"
      hidden
      frameborder="0"
      :src="invoiceSrc"
      type="application/pdf"
      width="100%"
      height="100%"
    />
    <!-- Hidden iframe used to print invoice -->
    <div class="text-left justify-start align-start">
      <!-- <span class="donation-flow__step-title mb-4 mt-8" style="display: block; text-align: center; font-size: 24px !important;">{{
        t('donationGuide.successView.title')
      }}</span> -->
      <v-dialog
        v-if="donation && donation.paymentSpecification"
        v-model="showReceipt"
        attach="body"
        data-cy="success-view-dialog"
        persistent
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
      >
        <v-card :loading="loadingReceipt">
          <v-app-bar color="secondary" dense>
            <v-btn data-cy="success-view-close-receipt-btn" icon @click="showReceipt = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title>{{ t('success.receipt') }}</v-toolbar-title>
          </v-app-bar>
          <v-card class="popup">
            <v-alert
              v-model="error"
              data-cy="success-view-alert"
              border="start"
              type="error"
              class="mt-2"
              colored-border
              elevation="2"
            >
              {{ t('success.receiptError') }}
            </v-alert>
            <!-- A4 aspect ratio -->
            <iframe
              frameborder="0"
              :src="receiptSrc"
              type="application/pdf"
              width="100%"
              style="height: calc(99vh - 70px)"
            />
          </v-card>
        </v-card>
      </v-dialog>
      <v-card v-if="donation && donation.paymentSpecification" style="box-shadow: none">
        <v-card :class="compact && 'elevation-0 pa-0'">
          <v-slide-x-transition mode="out-in" :class="compact && 'elevation-0 pa-0'">
            <div v-if="loading" class="text-center">
              <v-progress-circular color="primary" indeterminate />
            </div>
            <div v-else>
              <div class="text-center">
                <div class="success-checkmark">
                  <v-icon size="63" color="#047481">mdi-check-circle</v-icon>
                </div>
                <p class="my-8 success-text">{{ isPaylink ? t('success.paylink') : t('donationGuide.successView.donationSent') }}</p>
              </div>

              <!-- Delivery method specific messaging -->
              <p class="success-subtitle">{{ deliveryMessage }}</p>

              <invoice-component v-if="isInvoice && displayInvoice" reduced-header hide-icon class="mt-4" />

              <!-- Recap of the donation -->
              <div class="mt-10">
                <p class="recap-heading">{{ t('donationGuide.successView.recapHeading') }}</p>

                <div class="donation-summary">
                  <div v-if="isMemoryGift" class="donation-summary__row">
                    <span class="donation-summary__label">{{ t('summary.recipient') }}</span>
                    <span class="donation-summary__value">{{ originalRecipientName }}</span>
                  </div>

                  <!-- <div v-if="isMemoryGift" class="donation-summary__row">
                    <span class="donation-summary__label">{{ t('donationGuide.successView.shareGreeting') }}</span>
                    <span class="donation-summary__value">{{ shareGreeting ? t('generic.labels.yes') : t('generic.labels.no') }}</span>
                  </div> -->

                  <div class="donation-summary__row">
                    <span class="donation-summary__label">{{ t('summary.charity') }}</span>
                    <span class="donation-summary__value">{{ charityName }}</span>
                  </div>

                  <v-divider class="my-6" />

                  <div class="donation-summary__row">
                    <span class="donation-summary__label">{{ t('donationGuide.successView.donationAmount') }}</span>
                    <span class="donation-summary__value">{{ formatCurrency(causeAmount) }}</span>
                  </div>

                  <v-divider class="my-6" />

                  <div v-if="confirmationEmail" class="donation-summary__row">
                    <span class="donation-summary__label">{{ t('donationGuide.summaryView.paymentDetails.confirmationTo') }}</span>
                    <span class="donation-summary__value">{{ confirmationEmail }}</span>
                  </div>

                  <div class="donation-summary__row">
                    <span class="donation-summary__label">{{ t('donationGuide.summaryView.paymentDetails.delivery') }}</span>
                    <span class="donation-summary__value">{{ t('donationGuide.summaryView.paymentDetails.free') }}</span>
                  </div>

                  <div class="donation-summary__row">
                    <span class="donation-summary__label">{{ t('donationGuide.successView.serialNumber') }}</span>
                    <span class="donation-summary__value" data-cy="success-serial-chip">{{ donation.serialnumber }}</span>
                  </div>
                </div>

                <p class="serial-tracking-note">
                  {{ t('donationGuide.successView.trackProgress') }}
                  <a href="https://mingava.se" target="_blank" rel="noopener noreferrer">mingava.se</a>
                </p>
              </div>
            </div>
            
          </v-slide-x-transition>
          <v-card-actions class="donation-flow__buttons mt-8">
            <v-btn
              v-if="isDigitalPayment || (isPaylink && isPaid)"
              data-cy="success-view-toggle-receipt-btn"
              class="mr-4"
              :loading="loadingReceipt"
              :aria-label="t('aria.label.showReceipt')"
              @click="toggleReceipt"
            >
              {{ t('success.receipt') }}
              <v-icon class="ml-2"> mdi-text-box-check-outline </v-icon>
            </v-btn>
            <v-btn
              v-if="isDownloadInvoice"
              data-cy="success-view-toggle-invoice-btn"
              :aria-label="t('aria.label.printInvoice')"
              color="primary"
              class="mr-4"
              :loading="loadingInvoice"
              @click="printPdf"
            >
              {{ t('success.invoicePrint') }}
              <v-icon class="ml-2"> mdi-printer </v-icon>
            </v-btn>
            <v-btn
              v-if="deliveryIsPrint"
              data-cy="success-view-toggle-delivery-print-btn"
              color="success"
              class="mr-4"
              :aria-label="t('aria.label.printGiftCertificate')"
              :loading="loadingCertificate"
              @click="printCertificate"
            >
              {{ t('success.printGiftCertificate') }}
              <v-icon class="ml-2"> mdi-charity </v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="requestSession"
              data-cy="success-view-reset-btn"
              id="successViewResetButton"
              variant="elevated"
              class="mr-4 success-cta-btn"
              color="primary"
              @click="reset"
            >
              {{ t('success.newDonation') }}
            </v-btn>
          </v-card-actions>
          <feedback-banner full-horizontal-separator />
        </v-card>
      </v-card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import store from '@/store';
import { BASE_URL, ENDPOINTS, MainApi, put, guideErrorMessage, guideHttpStatus } from '@/api/mainApi';
import { useCertificateOperations } from '@/composables/useCertificateOperations';
import { useInvoiceOperations } from '@/composables/useInvoiceOperations';
import { RequestSession } from '@/interfaces/requestSession';
import { Donation } from '@/interfaces/donation/donation';
import InvoiceComponent from '@/components/invoice/InvoiceComponent.vue';
import {
  isDownloadInvoice as isDownloadInvoiceFn,
  isInvoice as isInvoiceFn,
  isPayLink as isPayLinkFn,
  isDigitalPayment as isDigitalPaymentFn,
} from '@/classes/PaymentMethod';
import { Type } from '@/classes/DeliveryMethod';
import { isMemoryGift as isMemoryGiftFn } from '@/classes/DonationType';
import { AttributeType } from '@/enums/attributeType';
import { getCurrencyForLocation } from '@/utils/currency';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import FeedbackBanner from '@/components/FeedbackBanner.vue';
import matomo from '@/services/matomoService';
import { useLabel } from '@/composables/useLabel';
import { donationWithoutCertificatePhoto } from '@/composables/useDonationReset';

const { t } = useLabel();
const { getCertificatePrintUrl } = useCertificateOperations();
const { getInvoiceDocumentUrl } = useInvoiceOperations();

defineProps<{
  compact?: boolean;
}>();

const emit = defineEmits(['reset']);

const loading = ref(false);
const error = ref(false);
const showReceipt = ref(false);
const loadingReceipt = ref(false);
const loadingInvoice = ref(false);
const loadingCertificate = ref(false);
const printIframe = ref<HTMLIFrameElement | null>(null);
const displayInvoice = ref(false);

const steps = computed(() => {
  const getDonationStepFlowVersion = (): DonationGuideVersion => {
    return store.state.donationGuideVersion;
  };
  return getDonationStepFlowVersion() === DonationGuideVersion.V1 ? stepV1 : stepV2;
});

const requestSession = computed((): RequestSession => store.state.requestSession!);

const donation = computed((): Donation => store.state.donation! as Donation);

const receiptSrc = computed((): string => {
  return (
    BASE_URL +
    '/' +
    MainApi +
    '/' +
    ENDPOINTS.DONATIONS +
    '/' +
    donation.value.serialnumber +
    '/' +
    ENDPOINTS.RECEIPT
  );
});

const invoiceSrc = computed((): string => {
  const sn = donation.value?.serialnumber;
  return sn ? getInvoiceDocumentUrl(sn, false, 'inline') : '';
});

const isDownloadInvoiceComputed = computed((): boolean => {
  return !!(
    donation.value.paymentSpecification &&
    donation.value.paymentSpecification.paymentMethod &&
    isDownloadInvoiceFn(donation.value.paymentSpecification.paymentMethod.type)
  );
});

const isInvoiceComputed = computed((): boolean => {
  return !!(
    donation.value.paymentSpecification &&
    donation.value.paymentSpecification.paymentMethod &&
    isInvoiceFn(donation.value.paymentSpecification.paymentMethod.type)
  );
});

const isDigitalPaymentComputed = computed((): boolean => {
  return !!(
    donation.value.paymentSpecification &&
    donation.value.paymentSpecification.paymentMethod &&
    isDigitalPaymentFn(donation.value.paymentSpecification.paymentMethod.type)
  );
});

const isPaylinkComputed = computed((): boolean => {
  return !!(
    donation.value.paymentSpecification &&
    donation.value.paymentSpecification.paymentMethod &&
    isPayLinkFn(donation.value.paymentSpecification.paymentMethod.type)
  );
});

// expose stable names used by templates
const isDownloadInvoice = isDownloadInvoiceComputed;
const isInvoice = isInvoiceComputed;
const isDigitalPayment = isDigitalPaymentComputed;
const isPaylink = isPaylinkComputed;

const isPaid = computed((): boolean => {
  return (
    donation.value &&
    donation.value.paymentSpecification &&
    donation.value.paymentSpecification.paid
  );
});

const deliveryIsPrint = computed((): boolean => {
  return !!(
    donation.value &&
    donation.value.paymentSpecification &&
    donation.value.deliveryMethod &&
    donation.value.deliveryMethod.type === Type.PRINT
  );
});

const deliveryMethodType = computed((): string => {
  return donation.value?.deliveryMethod?.type || '';
});

// ----- Recap of the donation (mirrors the checkout summary) -----

const getAttributeValueById = (id: number): string => {
  const attributeValue = donation.value?.attributeValues?.find((a) => a.attributeId === id);
  return attributeValue ? attributeValue.value : '';
};

const isMemoryGift = computed((): boolean =>
  donation.value?.donationType?.type ? isMemoryGiftFn(donation.value.donationType.type) : false
);

const giftCardRecipientName = computed(
  () =>
    getAttributeValueById(AttributeType.SUBJECT_NAME) ||
    donation.value?.donationCase?.caseInfo?.name ||
    ''
);

const originalRecipientName = computed(() => {
  const fromDonationCase = donation.value?.donationCase?.caseInfo?.name?.trim();
  if (fromDonationCase) {
    return fromDonationCase;
  }
  const donationCases = requestSession.value?.donationCases || [];
  if (donationCases.length === 1) {
    return donationCases[0]?.caseInfo?.name?.trim() || '';
  }
  return giftCardRecipientName.value;
});

const shareGreeting = computed((): boolean => !!donation.value?.sharePersonalInfoWithOrganization);

const charityName = computed(
  () =>
    donation.value?.donationCause?.organizationGroup?.name ||
    donation.value?.donationCause?.name ||
    requestSession.value?.organizationGroup?.name ||
    ''
);

const causeAmount = computed((): number => donation.value?.paymentSpecification?.causeAmount || 0);

const confirmationEmail = computed((): string => donation.value?.donor?.email || '');

const deliveryMessage = computed((): string => {
  switch (deliveryMethodType.value) {
    case Type.POST:
      return t('donationGuide.successView.sentByPost') as string;
    case Type.PRINT:
      return t('donationGuide.successView.readyToPrint') as string;
    case Type.EXTERNAL_AGENT:
      return t('donationGuide.successView.externalPartner') as string;
    default:
      return t('donationGuide.successView.emailConfirmation') as string;
  }
});

const formatCurrency = (amount: number | null): string => {
  if (amount === null || amount === undefined) return '';
  const currency = getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN');
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'code',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const currentStep = computed(() => {
  return store.state.currentStep;
});

watch(
  currentStep,
  (newVal) => {
    if (newVal === steps.value.SUCCESS) {
      if (isInvoiceComputed.value) {
        displayInvoice.value = true;
      }
      const current = store.state.donation;
      if (current?.imageKey || current?.customCertificateImageUrl) {
        store.commit('updateDonation', donationWithoutCertificatePhoto(current));
      }
      matomo.trackEvent('donations_completed', 'donation_finish', 'donations_completed');
    }
  },
  { immediate: true }
);

const toggleReceipt = (): void => {
  showReceipt.value = !showReceipt.value;
  matomo.trackEvent('donations_receipt_downloaded', 'donation_finish', 'donations_receipt_downloaded');
};

const reset = () => {
  emit('reset');
  matomo.trackEvent('donations_new_donation_started', 'donation_finish', 'donations_new_donation_started');
};

const printPdf = (): void => {
  const iframe = printIframe.value as HTMLIFrameElement | null;
  if (iframe && iframe.contentWindow) {
    try {
      iframe.focus();
      iframe.contentWindow.print();
    } catch {
      // ignore errors printing
    }
  }
  matomo.trackEvent('donations_receipt_downloaded', 'donation_finish', 'donations_receipt_downloaded');
};

const printCertificate = (): void => {
  loadingCertificate.value = true;
  // Open the window synchronously inside the user-gesture handler so Safari
  // doesn't treat it as a popup (Safari blocks window.open inside .then()).
  const win: Window | null = window.open('', '_blank');
  const externalEntityId = donation.value?.intermediary?.externalSource?.externalEntityId || null;
  put(ENDPOINTS.PRINT + '/' + ENDPOINTS.DONATIONS + '/' + donation.value.serialnumber, {
    externalEntityId,
  })
    .then(() => {
      if (win) {
        win.location.href = getCertificatePrintUrl(donation.value.serialnumber);
        win.focus();
      }
      matomo.trackEvent('donations_receipt_downloaded', 'donation_finish', 'donations_receipt_downloaded');
    })
    .catch((error) => {
      if (win) win.close();
      window.log?.error('[Guide] success.receipt — Failed to download receipt', {
        message: guideErrorMessage(error),
        httpStatus: guideHttpStatus(error),
        serialnumber: donation.value?.serialnumber,
      });
      store.commit('setSnackbar', {
        active: true,
        color: 'error',
        text: t('success.printGiftCertificateError'),
      });
    })
    .finally(() => {
      loadingCertificate.value = false;
    });
};
</script>
<style>
.success-checkmark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 14px;
}

.success-text {
  font-family: "Lato", sans-serif;
  font-weight: 800;
  font-size: 28px;
  color: #1f2a37;
}

.success-subtitle {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #6b7280;
  margin-top: 24px;
}

.success-note {
  font-family: "Inter", sans-serif;
  font-weight: 500;
}

.recap-heading {
  font-family: "Lato", sans-serif;
  font-weight: 800;
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 8px;
}

.success-view .donation-summary {
  padding: 16px 0;
}

.success-view .donation-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 5px;
}

.success-view .donation-summary__label {
  font-family: "Lato", sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #374151;
}

.success-view .donation-summary__value {
  font-family: "Lato", sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #374151;
  text-align: right;
  line-height: 155%;
}

.success-view .donation-flow__buttons .success-cta-btn {
  min-width: unset;
  width: auto;
}

.success-view .serial-tracking-note {
  font-family: "Lato", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
  margin-top: 16px;
}

.success-view .serial-tracking-note a {
  color: #047481;
  font-weight: 700;
  text-decoration: underline;
}

/* Stack the actions once the row no longer fits, as the step buttons do */
@media (max-width: 600px) {
  .success-view .donation-flow__buttons {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 12px;
  }

  .success-view .donation-flow__buttons .v-btn,
  .success-view .donation-flow__buttons .success-cta-btn {
    width: 100%;
    margin-inline: 0 !important;
  }

  .success-view .donation-flow__buttons .v-spacer {
    display: none !important;
  }

  /* Full width, the trailing icon would push the label off centre, so pin it
     to the edge and let the label centre in the button itself */
  .success-view .donation-flow__buttons .v-btn .v-btn__content {
    position: relative;
    width: 100%;
    justify-content: center;
    padding-inline: 32px;
  }

  .success-view .donation-flow__buttons .v-btn .v-btn__content .v-icon {
    position: absolute;
    inset-inline-end: 0;
    margin-inline-start: 0 !important;
  }
}
</style>
